import React, { useEffect, useMemo, useState } from 'react';

export default function BaziReadingView({ detailedReading, t }) {
  const [activeTab, setActiveTab] = useState('');
  const [datasetCopyStatus, setDatasetCopyStatus] = useState('');

  const readingTabs = useMemo(() => {
    const baseTabs = detailedReading?.sections?.map((section) => ({ id: section.id, label: section.title })) || [];
    if (detailedReading?.dataset) {
      baseTabs.push({ id: 'dataset', label: detailedReading.dataset.label });
    }
    return baseTabs;
  }, [detailedReading]);

  useEffect(() => {
    setDatasetCopyStatus('');
  }, [detailedReading]);

  useEffect(() => {
    if (!readingTabs.length) return;
    setActiveTab((previous) => {
      if (previous && readingTabs.some((tab) => tab.id === previous)) return previous;
      return readingTabs[0].id;
    });
  }, [readingTabs]);

  const handleTabKeyDown = (event, index) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const nextIndex = (index + direction + readingTabs.length) % readingTabs.length;
    setActiveTab(readingTabs[nextIndex].id);
  };

  const handleCopyDataset = (dataset) => {
    if (!dataset?.data) return;
    const onSuccess = () => setDatasetCopyStatus(dataset.copied || '');
    const onFail = () => setDatasetCopyStatus(dataset.failed || '');
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(dataset.data).then(onSuccess).catch(onFail);
      return;
    }
    onFail();
  };

  if (!detailedReading || readingTabs.length === 0) return null;

  return (
    <>
      <div className="bazi-tabs" role="tablist" aria-label={t.tabsLabel}>
        {readingTabs.map((tab, index) => (
          <button
            key={tab.id}
            id={`tab-${tab.id}`}
            className={`bazi-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bazi-panel" role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
        <h3 className="bazi-section-title">{detailedReading.title}</h3>
        <div className="bazi-reading">
          <div className="bazi-reading-intro">
            <h4>{t.detailedReadingTitle}</h4>
            <ul>
              {detailedReading.intro.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          {readingTabs.map((tab) => {
            const section = detailedReading.sections.find((item) => item.id === tab.id);
            if (activeTab !== tab.id) return null;

            if (tab.id === 'dataset' && detailedReading.dataset) {
              return (
                <div key="dataset" className="bazi-reading-section">
                  <div className="bazi-reading-callout">
                    <div className="bazi-dataset-header">
                      <strong>{detailedReading.dataset.label}</strong>
                      <button
                        type="button"
                        className="bazi-button-secondary"
                        onClick={() => handleCopyDataset(detailedReading.dataset)}
                      >
                        {detailedReading.dataset.copyCta}
                      </button>
                      {datasetCopyStatus && (
                        <span className="bazi-copy-status">{datasetCopyStatus}</span>
                      )}
                    </div>
                    <pre className="bazi-dataset-preview">{detailedReading.dataset.data}</pre>
                    {detailedReading.dataset.help && (
                      <p className="bazi-reading-footnote">{detailedReading.dataset.help}</p>
                    )}
                  </div>
                </div>
              );
            }

            if (!section) return null;
            return (
              <div key={section.id} className="bazi-reading-section">
                <h4>{section.title}</h4>
                {section.paragraphs?.map((text) => (
                  <p key={text}>{text}</p>
                ))}
                {section.blocks?.map((block) => (
                  <div key={block.label} className="bazi-reading-block">
                    <h5>{block.label}</h5>
                    <ul>
                      {block.body.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
                {section.summary && (
                  <div className="bazi-reading-summary">
                    <p className="bazi-reading-summary-title">Tổng hợp Tứ Trụ</p>
                    <ul>
                      {section.summary.map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {section.table && (
                  <div className="bazi-reading-table">
                    <table>
                      <thead>
                        <tr>
                          {section.table.headers.map((header) => (
                            <th key={header}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.table.rows.map((row) => (
                          <tr key={row.join('-')}>
                            {row.map((cell, cellIndex) => (
                              <td
                                key={`${section.table.headers[cellIndex]}-${cell}`}
                                data-label={section.table.headers[cellIndex]}
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {Array.isArray(section.callout) && section.callout.length > 0 && (
                  <div className="bazi-reading-callout">
                    {section.callout.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                )}
                {section.dataset && (
                  <div className="bazi-reading-callout">
                    <div className="bazi-dataset-header">
                      <strong>{section.dataset.label}</strong>
                      <button
                        type="button"
                        className="bazi-button-secondary"
                        onClick={() => handleCopyDataset(section.dataset)}
                      >
                        {section.dataset.copyCta}
                      </button>
                      {datasetCopyStatus && (
                        <span className="bazi-copy-status">{datasetCopyStatus}</span>
                      )}
                    </div>
                    <pre className="bazi-dataset-preview">{section.dataset.data}</pre>
                    {section.dataset.help && (
                      <p className="bazi-reading-footnote">{section.dataset.help}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
